import { useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';

import { useTranslate } from '@growtherapy/i18n';

import { hideChromeToolbarPlaybackControls } from '../classes/ChromeToolbarPlaybackControlsSingleton';
import { logger } from '../datadog/logger';
import { toast } from '../toasts';
import { noop } from '../utils';
import type { RoomDelegator } from '../video-sdk';
import {
  DEFAULT_DATA_CHANNEL_APP_STATE,
  dataChannelAppStateAtom,
  roomAtom,
  useLeaveRoom,
  videoSDKAtom,
} from '../video-sdk';
import useScreenShareCallbacks from './controls/useScreenShareCallbacks';
import { useRevertVideoQualityCallback } from './controls/useVideoQualityCallbacks';

export type DisconnectTwilioRoomCallback = (options?: {
  shouldEndRoom?: boolean;
  connectionError?: Error;
  onError?: (error: unknown) => void;
  skipLeaveRoomAction?: boolean;
  /**
   * Whether to reset the video SDK app state regardless of whether leaving the
   * room succeeds or fails. This can be used in scenarios where the room is
   * already disconnected (e.g. due to a network issue) and we want to ensure
   * the app state is cleaned up.
   */
  alwaysResetAppState?: boolean;
}) => Promise<RoomDelegator | undefined>;

function useResetAppStateCallback() {
  const setVideoSDKContext = useSetAtom(videoSDKAtom);
  const revertVideoQuality = useRevertVideoQualityCallback();
  const setDataChannelAppState = useSetAtom(dataChannelAppStateAtom);

  return useCallback(
    function teardownRoom() {
      setVideoSDKContext((prev) => ({
        ...prev,
        room: undefined,
        participants: [],
        connectedAt: undefined,
        remoteMediaByParticipantId: {},
        remoteMediaStatusByParticipantId: {},
        shouldOverrideSuggestedRoomVideoQuality: false,
      }));
      revertVideoQuality();
      setDataChannelAppState(DEFAULT_DATA_CHANNEL_APP_STATE);
      // side effect
      hideChromeToolbarPlaybackControls();
    },
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );
}

/**
 * Disconnects from the video call room and clears relevant Jotai state.
 */
export function useDisconnectRoomCallback(): DisconnectTwilioRoomCallback {
  const t = useTranslate();
  const getRoom = useAtomCallback(useCallback((get) => get(roomAtom), []));
  const leaveRoom = useLeaveRoom();
  const { stopScreenShare } = useScreenShareCallbacks();
  const resetAppState = useResetAppStateCallback();

  return useCallback(
    async function disconnectFromRoom(options) {
      const room = getRoom();
      if (!room) return;

      let leaveError: Error | undefined = undefined;

      stopScreenShare();
      await (options?.skipLeaveRoomAction
        ? noop
        : leaveRoom({
            shouldEndRoom: options?.shouldEndRoom,
            connectionError: options?.connectionError,
          }).catch((error) => {
            leaveError = error as Error;
            toast.error(t('telehealth-core:errorsCore.thereWasAnIssueDisconnectingFromTheRoom'), {
              purpose: 'system',
            });
            logger.error(
              'Unable to disconnect from room',
              {
                roomSid: room.sid,
                localParticipantSid: room.localParticipant?.sid,
              },
              error as Error,
            );
            options?.onError?.(error);
          }));

      if (!leaveError || options?.alwaysResetAppState) {
        resetAppState();
      }

      return leaveError ? undefined : room;
    },
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );
}
